import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lava-refresh/artifacts/cr75vzod_logo%20kilolava.png";

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Pulizie', href: '/pulizie' },
  { label: 'Lavanderia', href: '/lavanderia' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/90 backdrop-blur-lg shadow-[0_4px_30px_rgb(0,0,0,0.05)]' 
            : 'bg-white/50 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <motion.img 
                src={LOGO_URL}
                alt="Kilolava Logo"
                className="h-16 w-auto"
                whileHover={{ scale: 1.02 }}
              />
            </Link>

            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className={`font-medium transition-colors duration-300 ${
                    location.pathname === link.href 
                      ? 'text-primary' 
                      : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Button
                data-testid="navbar-cta-button"
                className="bg-primary hover:bg-primary-hover text-white px-6 rounded-full shadow-[0_10px_30px_rgb(233,30,99,0.2)] hover:shadow-[0_15px_40px_rgb(233,30,99,0.3)] transition-all duration-300"
                asChild
              >
                <a href="https://api.whatsapp.com/send/?phone=393513416151" target="_blank" rel="noopener noreferrer">
                  Contattaci
                </a>
              </Button>
            </div>

            <button
              data-testid="mobile-menu-button"
              className="md:hidden p-2 rounded-lg hover:bg-primary/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-text-primary" />
              ) : (
                <Menu className="w-6 h-6 text-text-primary" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-primary pt-24 md:hidden"
          >
            <div className="flex flex-col items-center gap-8 p-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className="font-heading text-2xl font-bold text-white hover:text-white/80 transition-colors"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <Button
                data-testid="mobile-cta-button"
                size="lg"
                className="bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg rounded-full w-full max-w-xs mt-4"
                asChild
              >
                <a href="https://api.whatsapp.com/send/?phone=393513416151" target="_blank" rel="noopener noreferrer">
                  Contattaci su WhatsApp
                </a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
